import { Fragment, useEffect, useMemo, useState } from 'react';
import Card from '@material-ui/core/Card';
import List from '@material-ui/core/List';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import SettingsIcon from '@material-ui/icons/Settings';
import {
  configApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import {
  catalogReactTranslationRef,
  useEntityList,
} from '@backstage/plugin-catalog-react';
import { useIsAdmin } from '../agent/useIsAdmin';
import {
  EntityInfraScopeFilter,
  InfraScopeKind,
} from './EntityInfraScopeFilter';
import { useInfraScopeCounts } from './useInfraScopeCounts';

const useStyles = makeStyles(
  theme => ({
    root: {
      backgroundColor: 'rgba(0, 0, 0, .11)',
      boxShadow: 'none',
      margin: theme.spacing(1, 0, 1, 0),
    },
    title: {
      margin: theme.spacing(1, 0, 0, 1),
      textTransform: 'uppercase',
      fontSize: 12,
      fontWeight: 'bold',
    },
    listIcon: {
      minWidth: 30,
      color: theme.palette.text.primary,
    },
    menuItem: {
      minHeight: theme.spacing(6),
    },
    groupWrapper: {
      margin: theme.spacing(1, 1, 2, 1),
    },
  }),
  { name: 'CatalogReactUserListPicker' },
);

type ScopeItem = {
  id: InfraScopeKind;
  label: string;
  icon?: typeof SettingsIcon;
};

type PsInfraScopePickerProps = {
  initialScope?: InfraScopeKind;
};

function normalizeScope(raw: string | undefined): InfraScopeKind | undefined {
  if (raw === 'my' || raw === 'all') {
    return raw;
  }
  // Legacy query params / first iteration of this picker
  if (raw === 'owned' || raw === 'mine') {
    return 'my';
  }
  if (raw === 'team') {
    return 'all';
  }
  return undefined;
}

export const PsInfraScopePicker = ({
  initialScope = 'my',
}: PsInfraScopePickerProps) => {
  const classes = useStyles();
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const isAdmin = useIsAdmin();
  const { t } = useTranslationRef(catalogReactTranslationRef);
  const orgName =
    configApi.getOptionalString('organization.name') ??
    t('userListPicker.defaultOrgName');
  const {
    filters,
    updateFilters,
    queryParameters: { user: userParameter },
  } = useEntityList();

  const [identity, setIdentity] = useState<{ username: string }>();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const backstageIdentity = await identityApi.getBackstageIdentity();
      const username =
        backstageIdentity.userEntityRef.split('/').pop() ?? '';
      if (!cancelled) {
        setIdentity({ username });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [identityApi]);

  const queryScope = useMemo(() => {
    const raw = [userParameter].flat()[0];
    return normalizeScope(raw);
  }, [userParameter]);

  const [selectedScope, setSelectedScope] = useState<InfraScopeKind>(
    queryScope ?? initialScope,
  );

  useEffect(() => {
    if (queryScope) {
      setSelectedScope(queryScope);
    }
  }, [queryScope]);

  const { counts: filterCounts, loading: loadingCounts } = useInfraScopeCounts({
    username: identity?.username ?? '',
    isAdmin: isAdmin ?? false,
  });

  // All inventory is admin-only; drop team-wide All for PS/SA.
  useEffect(() => {
    if (isAdmin === false && selectedScope === 'all') {
      setSelectedScope('my');
    }
  }, [isAdmin, selectedScope]);

  useEffect(() => {
    if (!identity || isAdmin === undefined) {
      return;
    }
    const scopeFilter = new EntityInfraScopeFilter(
      selectedScope,
      identity.username,
      isAdmin,
    );
    updateFilters({
      user: scopeFilter as Parameters<typeof updateFilters>[0]['user'],
    });
  }, [identity, isAdmin, selectedScope, updateFilters]);

  if (!identity || isAdmin === undefined) {
    return null;
  }

  const filterGroups = [
    {
      name: 'Personal',
      items: [
        {
          id: 'my' as const,
          // Filter is by infrastructure.backstage.io/created-by, not spec.owner.
          label: 'Created by me',
          icon: SettingsIcon,
        },
      ],
    },
    ...(isAdmin
      ? [
          {
            name: orgName,
            items: [
              {
                id: 'all' as const,
                label: t('userListPicker.orgFilterAllLabel'),
              },
            ],
          },
        ]
      : []),
  ];

  const activeValue = (filters.user as EntityInfraScopeFilter | undefined)
    ?.value;

  return (
    <Card className={classes.root}>
      {filterGroups.map(group => (
        <Fragment key={group.name}>
          <Typography
            variant="subtitle2"
            component="span"
            className={classes.title}
          >
            {group.name}
          </Typography>
          <Card className={classes.groupWrapper}>
            <List
              disablePadding
              dense
              role="menu"
              aria-label={group.name}
            >
              {group.items.map((item, index) => (
                <MenuItem
                  key={item.id}
                  role="none presentation"
                  divider={index !== group.items.length - 1}
                  onClick={() => setSelectedScope(item.id)}
                  selected={item.id === activeValue}
                  className={classes.menuItem}
                  data-testid={`user-picker-${item.id}`}
                  tabIndex={0}
                >
                  {item.icon && (
                    <ListItemIcon className={classes.listIcon}>
                      <item.icon fontSize="small" />
                    </ListItemIcon>
                  )}
                  <ListItemText>
                    <Typography variant="body1">{item.label}</Typography>
                  </ListItemText>
                  <ListItemSecondaryAction>
                    {loadingCounts || !identity
                      ? '-'
                      : (filterCounts[item.id] ?? '-')}
                  </ListItemSecondaryAction>
                </MenuItem>
              ))}
            </List>
          </Card>
        </Fragment>
      ))}
    </Card>
  );
};
